/**
 * Engineering Intelligence API client — consumes frozen Phase-2 endpoints only.
 */
import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, buildApiHeaders, downloadCredentialQuery } from './api';
import { fetchWithTimeout } from '../lib/fetchWithTimeout';
import { pollIntervalMs } from '../lib/queryPolling';
import type {
  EiAuditExport,
  EiConsistencyEvent,
  EiContextPack,
  EiExecutionTracePayload,
  EiImplementationPackage,
  EiInvestigationRun,
  EiRepositoryBasis,
  EiMemoryHit,
  EiPatchPlan,
  EiValidationContract,
  EiValidationReport,
} from '../types/engineeringIntelligence';

/** UI flag: set VITE_EI_FRONTEND_V2=false to force legacy AMS presentation. */
export function eiFrontendEnabled(): boolean {
  const v = String(import.meta.env.VITE_EI_FRONTEND_V2 ?? 'true').toLowerCase();
  return v !== '0' && v !== 'false' && v !== 'off' && v !== 'no';
}

/** Phase 4 Investigation Experience — set VITE_EI_EXPERIENCE_V1=false to use legacy EI report panel. */
export function eiExperienceEnabled(): boolean {
  if (!eiFrontendEnabled()) return false;
  const v = String(import.meta.env.VITE_EI_EXPERIENCE_V1 ?? 'true').toLowerCase();
  return v !== '0' && v !== 'false' && v !== 'off' && v !== 'no';
}

/** Operator-friendly message for EI action failures (esp. LLM provider errors). */
export function formatEiActionError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err || 'request failed');
  const m = raw.toLowerCase();
  if (m.includes('cancelled') || m.includes('aborted')) {
    return 'Investigation stopped. Click Refresh, then Step or Run investigation to continue.';
  }
  if (m.includes('no completion choices') || m.includes('empty content')) {
    return (
      'The language model returned an empty response (provider hiccup). ' +
      'Your investigation progress was kept — click Run investigation or Step to continue.'
    );
  }
  if (m.includes('deadline exceeded') || m.includes('timed out') || m.includes('timeout')) {
    return (
      'The investigation request timed out while waiting on the model. ' +
      'Progress so far is saved — Refresh, then continue with Step or Run investigation.'
    );
  }
  if (m.includes('429') || m.includes('rate limit')) {
    return 'The model provider is rate-limiting requests. Wait a moment, then retry.';
  }
  if (m.includes('provider error')) {
    return `Model provider issue: ${raw.replace(/^provider error:\s*/i, '')}. Progress is saved — retry when ready.`;
  }
  return raw;
}

/** Multi-step investigation / package / execute can take several LLM turns. */
const EI_LONG_TIMEOUT_MS = 15 * 60_000;
const EI_STEP_TIMEOUT_MS = 3 * 60_000;
const EI_GET_TIMEOUT_MS = 45_000;

function eiBase(projectId: string, incidentId: string) {
  return `/projects/${projectId}/ams/incidents/${incidentId}/engineering-intelligence`;
}

/** Change-request-driven mission counterpart of eiBase (no AMS incident involved). */
function eiCrBase(projectId: string, crId: string) {
  return `/projects/${projectId}/change-requests/${crId}/engineering-intelligence`;
}

export const eiKeys = {
  latest: (projectId: string, incidentId: string) =>
    ['ei', 'latest', projectId, incidentId] as const,
  run: (projectId: string, incidentId: string, runId: string) =>
    ['ei', 'run', projectId, incidentId, runId] as const,
  package: (projectId: string, incidentId: string, runId: string) =>
    ['ei', 'package', projectId, incidentId, runId] as const,
  executionTrace: (projectId: string, incidentId: string, runId: string) =>
    ['ei', 'execution-trace', projectId, incidentId, runId] as const,
  validation: (projectId: string, incidentId: string, runId: string) =>
    ['ei', 'validation', projectId, incidentId, runId] as const,
  contextPack: (projectId: string, incidentId: string, runId: string) =>
    ['ei', 'context-pack', projectId, incidentId, runId] as const,
  validationContract: (projectId: string, incidentId: string, runId: string) =>
    ['ei', 'validation-contract', projectId, incidentId, runId] as const,
  consistencyEvents: (projectId: string, limit: number) =>
    ['ei', 'consistency-events', projectId, limit] as const,
  auditExport: (projectId: string, incidentId: string, runId: string) =>
    ['ei', 'audit-export', projectId, incidentId, runId] as const,
  memorySearch: (projectId: string, q: string) =>
    ['ei', 'memory-search', projectId, q] as const,
};

export async function fetchLatestEiRun(
  projectId: string,
  incidentId: string,
  opts?: { signal?: AbortSignal },
): Promise<EiInvestigationRun | null> {
  try {
    return await apiClient<EiInvestigationRun>(eiBase(projectId, incidentId), {
      timeoutMs: EI_GET_TIMEOUT_MS,
      signal: opts?.signal,
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (/404|not found|disabled|no (investigation )?run/i.test(msg)) return null;
    throw e;
  }
}

export async function fetchEiRun(
  projectId: string,
  incidentId: string,
  runId: string,
  opts?: { signal?: AbortSignal },
): Promise<EiInvestigationRun> {
  return apiClient<EiInvestigationRun>(`${eiBase(projectId, incidentId)}/runs/${runId}`, {
    timeoutMs: EI_GET_TIMEOUT_MS,
    signal: opts?.signal,
  });
}

export async function beginEiRun(
  projectId: string,
  incidentId: string,
  bootstrap?: Record<string, unknown>,
): Promise<EiInvestigationRun> {
  return apiClient<EiInvestigationRun>(`${eiBase(projectId, incidentId)}/begin`, {
    method: 'POST',
    body: JSON.stringify({ bootstrap: bootstrap ?? {} }),
  });
}

export async function stepEiRun(
  projectId: string,
  incidentId: string,
  runId: string,
  opts?: { signal?: AbortSignal },
): Promise<{ run?: EiInvestigationRun; advanced_to?: string; stopped_reason?: string }> {
  return apiClient(`${eiBase(projectId, incidentId)}/runs/${runId}/step`, {
    method: 'POST',
    body: '{}',
    timeoutMs: EI_STEP_TIMEOUT_MS,
    signal: opts?.signal,
  });
}

export async function runUntilInvestigationComplete(
  projectId: string,
  incidentId: string,
  runId: string,
  maxSteps = 24,
  opts?: { signal?: AbortSignal },
): Promise<{ run?: EiInvestigationRun }> {
  return apiClient(
    `${eiBase(projectId, incidentId)}/runs/${runId}/run-until-investigation-complete`,
    {
      method: 'POST',
      body: JSON.stringify({ max_steps: maxSteps }),
      timeoutMs: EI_LONG_TIMEOUT_MS,
      signal: opts?.signal,
    },
  );
}

export async function fetchImplementationPackage(
  projectId: string,
  incidentId: string,
  runId: string,
): Promise<EiImplementationPackage> {
  return apiClient(`${eiBase(projectId, incidentId)}/runs/${runId}/implementation-package`);
}

export async function approveImplementationPackage(
  projectId: string,
  incidentId: string,
  runId: string,
  decision: 'approve' | 'reject' | 'modify' = 'approve',
  note = '',
  packageHash?: string,
): Promise<EiInvestigationRun> {
  return apiClient(`${eiBase(projectId, incidentId)}/runs/${runId}/approve`, {
    method: 'POST',
    // Backend expects `action`; keep `decision` as a compat alias.
    // package_hash: the seal of the package the operator reviewed — the
    // backend refuses an approve whose package has changed since (409).
    body: JSON.stringify({ action: decision, decision, note, package_hash: packageHash }),
  });
}

export async function executeEiRun(
  projectId: string,
  incidentId: string,
  runId: string,
): Promise<{ run?: EiInvestigationRun }> {
  return apiClient(`${eiBase(projectId, incidentId)}/runs/${runId}/execute`, {
    method: 'POST',
    body: '{}',
    timeoutMs: EI_STEP_TIMEOUT_MS,
  });
}

/** Testing decision on the current results; `waive` records an audited waiver (needs sign_off). */
export interface EiAcceptTestingInput {
  /** The package whose results the operator reviewed — the backend refuses any other (409). */
  packageHash: string;
  waive?: boolean;
  reason?: string;
  /**
   * Content SHA-256 of the supplied integration test the reviewer read. Required
   * (and checked by the backend) when the results rely on supplied evidence.
   */
  acknowledgeSuppliedEvidence?: string;
  /**
   * Accept completed results that did not fully pass (needs sign_off). The
   * backend refuses unless reportHash is the reviewed evidence and every
   * current risk ID is acknowledged. Records a risk acceptance; executes nothing.
   */
  acceptRisks?: boolean;
  reportHash?: string;
  acknowledgedRisks?: string[];
  /**
   * ENSO UTC decision (backend acceptTestingBody): the reviewed report hash,
   * exactly the gates the review lists to acknowledge (plus its blocking
   * gates for acceptRisks), and a reason when any gate is acknowledged.
   * Not needed for a waiver.
   */
  ensoUtcReportHash?: string;
  ensoUtcAcknowledgedGates?: string[];
  ensoUtcReason?: string;
}

/** One operator-supplied integration test submitted as evidence for the package on the run. */
export interface EiSupplyEvidenceInput {
  /** The package the operator reviewed — the backend refuses any other. */
  packageHash: string;
  /** Where the test sits in the isolated copies, relative to the repository root. */
  path: string;
  source: string;
  /** SHA-256 of `source` as reviewed; the backend recomputes it and refuses a mismatch. */
  contentSha256: string;
  note?: string;
}

export interface EiSupplyEvidenceResult {
  evidence_id: string;
  status: string;
  path: string;
  content_sha256: string;
  package_hash: string;
  submitted_by: string;
  references_changed?: string[];
}

function supplyEvidenceBody(input: EiSupplyEvidenceInput): string {
  return JSON.stringify({
    package_hash: input.packageHash,
    path: input.path,
    source: input.source,
    content_sha256: input.contentSha256,
    note: input.note ?? '',
  });
}

export async function supplyEiIntegrationEvidence(
  projectId: string,
  incidentId: string,
  runId: string,
  input: EiSupplyEvidenceInput,
): Promise<EiSupplyEvidenceResult> {
  return apiClient(`${eiBase(projectId, incidentId)}/runs/${runId}/supplied-integration-evidence`, {
    method: 'POST',
    body: supplyEvidenceBody(input),
  });
}

export async function supplyEiIntegrationEvidenceForChangeRequest(
  projectId: string,
  crId: string,
  runId: string,
  input: EiSupplyEvidenceInput,
): Promise<EiSupplyEvidenceResult> {
  return apiClient(`${eiCrBase(projectId, crId)}/runs/${runId}/supplied-integration-evidence`, {
    method: 'POST',
    body: supplyEvidenceBody(input),
  });
}

/** Start Tests (or rerun) on the run's own working copy. Never promotes. */
export async function startEiTesting(projectId: string, incidentId: string, runId: string): Promise<EiInvestigationRun> {
  return apiClient(`${eiBase(projectId, incidentId)}/runs/${runId}/start-testing`, { method: 'POST', body: '{}' });
}

/** Accept the current Testing results, or record an audited waiver. Never promotes. */
export async function acceptEiTesting(
  projectId: string,
  incidentId: string,
  runId: string,
  input: EiAcceptTestingInput,
): Promise<EiInvestigationRun> {
  return apiClient(`${eiBase(projectId, incidentId)}/runs/${runId}/accept-testing`, {
    method: 'POST',
    body: JSON.stringify({
      package_hash: input.packageHash,
      waive: !!input.waive,
      reason: input.reason ?? '',
      acknowledge_supplied_evidence: input.acknowledgeSuppliedEvidence ?? '',
      accept_risks: !!input.acceptRisks,
      report_hash: input.reportHash ?? '',
      acknowledged_risks: input.acknowledgedRisks ?? [],
      enso_utc_report_hash: input.ensoUtcReportHash ?? '',
      enso_utc_acknowledged_gates: input.ensoUtcAcknowledgedGates ?? [],
      enso_utc_reason: input.ensoUtcReason ?? '',
    }),
  });
}

export async function fetchExecutionTrace(
  projectId: string,
  incidentId: string,
  runId: string,
): Promise<EiExecutionTracePayload> {
  return apiClient(`${eiBase(projectId, incidentId)}/runs/${runId}/execution-trace`);
}

export async function fetchValidationReport(
  projectId: string,
  incidentId: string,
  runId: string,
): Promise<EiValidationReport> {
  return apiClient(`${eiBase(projectId, incidentId)}/runs/${runId}/validation-report`);
}

export async function fetchContextPack(
  projectId: string,
  incidentId: string,
  runId: string,
): Promise<EiContextPack> {
  return apiClient(`${eiBase(projectId, incidentId)}/runs/${runId}/context-pack`);
}

export async function fetchValidationContract(
  projectId: string,
  incidentId: string,
  runId: string,
): Promise<EiValidationContract | null> {
  try {
    return await apiClient(`${eiBase(projectId, incidentId)}/runs/${runId}/validation-contract`);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (/404|not found|not expos|incomplete/i.test(msg)) return null;
    throw e;
  }
}

export async function fetchConsistencyEvents(
  projectId: string,
  limit = 40,
): Promise<{ events?: EiConsistencyEvent[]; data?: EiConsistencyEvent[] } | EiConsistencyEvent[]> {
  return apiClient(`/projects/${projectId}/engineering-intelligence/consistency-events?limit=${limit}`);
}

export async function fetchAuditExport(
  projectId: string,
  incidentId: string,
  runId: string,
): Promise<EiAuditExport> {
  return apiClient(`${eiBase(projectId, incidentId)}/runs/${runId}/audit-export`);
}

export async function runUntilResolved(
  projectId: string,
  incidentId: string,
  runId: string,
  maxSteps = 48,
): Promise<{ run?: EiInvestigationRun }> {
  return apiClient(`${eiBase(projectId, incidentId)}/runs/${runId}/run-until-resolved`, {
    method: 'POST',
    body: JSON.stringify({ max_steps: maxSteps }),
    timeoutMs: EI_LONG_TIMEOUT_MS,
  });
}

export type HumanGuidanceDecision =
  | 'approve_relocate'
  | 'use_alternate'
  | 'confirm'
  | 'reject'
  | 'provide_evidence'
  | 'acknowledge_close'
  | 'keep_changes'
  | 'approve_rollback'
  | 'waive_tests_and_close'
  | 'rerun_tests'
  | 'resume'
  | 'pause'
  | 'custom'
  | 'unknown';

export type HumanGuidanceAnswer = {
  question: string;
  answer: string;
  choice?: string;
};

export async function submitHumanGuidance(
  projectId: string,
  incidentId: string,
  runId: string,
  body: {
    decision: HumanGuidanceDecision;
    message?: string;
    choice?: string;
    operator?: string;
    resume?: boolean;
    answers?: HumanGuidanceAnswer[];
  },
): Promise<{ run?: EiInvestigationRun; stopped_reason?: string }> {
  return apiClient(`${eiBase(projectId, incidentId)}/runs/${runId}/human-guidance`, {
    method: 'POST',
    body: JSON.stringify(body),
    timeoutMs: EI_LONG_TIMEOUT_MS,
  });
}

/** Approve designing a patch for a gate-parked incident run against the
 * repository the RCA investigated, whose deployed revision is not verified.
 * Records no attribution or deployment; the resulting package still cannot be
 * approved or executed until a matching deployed revision is verified. */
export async function approveSnapshotEngineering(
  projectId: string,
  incidentId: string,
  runId: string,
  reason?: string,
  /** Proceed with Engineering although the confirmed owner is not ENGINEERING (ownership is not changed). */
  explicitEngineeringRequest = false,
  /** Ask for a candidate patch for review although the RCA's evidence gates fail. */
  candidatePatch = false,
): Promise<{ run_id: string; recorded: boolean; resumed: boolean; state?: string; repository_basis?: EiRepositoryBasis | null }> {
  return apiClient(`${eiBase(projectId, incidentId)}/runs/${runId}/snapshot-engineering`, {
    method: 'POST',
    body: JSON.stringify({ confirm_unverified_revision: true, reason: reason ?? '', explicit_engineering_request: explicitEngineeringRequest, candidate_patch: candidatePatch }),
  });
}

/** Explicitly request Engineering for an incident whose confirmed owner is not
 * ENGINEERING (ownership is not changed): begins or reuses the incident's run
 * and records the request as its snapshot engineering opt-in. */
export async function requestIncidentEngineering(
  projectId: string,
  incidentId: string,
  reason?: string,
  candidatePatch = false,
): Promise<{ run_id: string; state?: string; begun: boolean; recorded: boolean; resumed: boolean; repository_basis?: EiRepositoryBasis | null }> {
  return apiClient(`${eiBase(projectId, incidentId)}/request`, {
    method: 'POST',
    body: JSON.stringify({ confirm_unverified_revision: true, reason: reason ?? '', candidate_patch: candidatePatch }),
  });
}

/** Record the operator's review that the deployed revision the RCA now
 * verified is the basis of the run's sealed snapshot-basis package. Approval of
 * that package is refused until this review names its current seal. */
export async function reviewSnapshotRevision(
  projectId: string,
  incidentId: string,
  runId: string,
  deployedRevision: string,
  packageHash: string,
): Promise<{ run_id: string; recorded: boolean }> {
  return apiClient(`${eiBase(projectId, incidentId)}/runs/${runId}/snapshot-engineering/review`, {
    method: 'POST',
    body: JSON.stringify({ deployed_revision: deployedRevision, package_hash: packageHash }),
  });
}

/** Operator-only manual resume of a PLATFORM_BLOCKED run (resets its retry count). */
export async function resumePlatformBlockedRun(
  projectId: string,
  run: { id?: string; incident_id?: string; change_request_id?: string },
): Promise<{ run_id: string; state: string }> {
  const base = run.change_request_id
    ? eiCrBase(projectId, run.change_request_id)
    : eiBase(projectId, run.incident_id || '');
  return apiClient(`${base}/runs/${run.id}/platform-resume`, { method: 'POST', body: '{}' });
}

/** Change-request-driven mission counterpart of submitHumanGuidance — same
 * backend route family (main.go registers both the incident- and
 * change-request-scoped human-guidance routes against the same handler). */
export async function submitHumanGuidanceForChangeRequest(
  projectId: string,
  crId: string,
  runId: string,
  body: {
    decision: HumanGuidanceDecision;
    message?: string;
    choice?: string;
    operator?: string;
    resume?: boolean;
    answers?: HumanGuidanceAnswer[];
  },
): Promise<{ run?: EiInvestigationRun; stopped_reason?: string }> {
  return apiClient(`${eiCrBase(projectId, crId)}/runs/${runId}/human-guidance`, {
    method: 'POST',
    body: JSON.stringify(body),
    timeoutMs: EI_LONG_TIMEOUT_MS,
  });
}

export async function searchEngineeringMemory(
  projectId: string,
  opts: { q?: string; failure_domain?: string; validation_outcome?: string; limit?: number } = {},
): Promise<{ memories: EiMemoryHit[]; count: number }> {
  const p = new URLSearchParams();
  if (opts.q) p.set('q', opts.q);
  if (opts.failure_domain) p.set('failure_domain', opts.failure_domain);
  if (opts.validation_outcome) p.set('validation_outcome', opts.validation_outcome);
  if (opts.limit) p.set('limit', String(opts.limit));
  const qs = p.toString();
  return apiClient(`/projects/${projectId}/engineering-memory/search${qs ? `?${qs}` : ''}`);
}

function invalidateEi(qc: ReturnType<typeof useQueryClient>, projectId: string, incidentId: string) {
  void qc.invalidateQueries({ queryKey: ['ei', 'latest', projectId, incidentId] });
  void qc.invalidateQueries({ queryKey: ['ei', 'run', projectId, incidentId] });
  void qc.invalidateQueries({ queryKey: ['ei', 'package', projectId, incidentId] });
  void qc.invalidateQueries({ queryKey: ['ei', 'execution-trace', projectId, incidentId] });
  void qc.invalidateQueries({ queryKey: ['ei', 'validation', projectId, incidentId] });
}

export function useLatestEiRun(
  projectId: string,
  incidentId: string | null | undefined,
  opts?: { live?: boolean },
) {
  const live = Boolean(opts?.live);
  return useQuery({
    queryKey: eiKeys.latest(projectId, incidentId || ''),
    queryFn: ({ signal }) => fetchLatestEiRun(projectId, incidentId!, { signal }),
    enabled: eiFrontendEnabled() && Boolean(projectId && incidentId),
    // While a long Run/Step POST is in flight, keep pulling the saved run so the panel
    // updates mid-investigation (evidence, state, hypotheses) instead of waiting for POST end.
    refetchInterval: (q) => {
      if (live) return 2_000;
      const state = q.state.data?.state || '';
      if (
        /EXECUTING|VALIDATING|ENGINEERING_DESIGN|COLLECTING|GATHERING|FORMING|SELF_REVIEW|PROVISIONAL|DIAGNOS|INVESTIGATION/.test(
          state,
        )
      ) {
        return 2_500;
      }
      return 12_000;
    },
    refetchIntervalInBackground: live,
    retry: false,
  });
}

export function useEiRun(projectId: string, incidentId: string | null | undefined, runId: string | null | undefined) {
  return useQuery({
    queryKey: eiKeys.run(projectId, incidentId || '', runId || ''),
    queryFn: ({ signal }) => fetchEiRun(projectId, incidentId!, runId!, { signal }),
    enabled: eiFrontendEnabled() && Boolean(projectId && incidentId && runId),
    refetchInterval: () => pollIntervalMs(8_000),
    retry: false,
  });
}

export function useEiImplementationPackage(
  projectId: string,
  incidentId: string | null | undefined,
  runId: string | null | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: eiKeys.package(projectId, incidentId || '', runId || ''),
    queryFn: () => fetchImplementationPackage(projectId, incidentId!, runId!),
    enabled: eiFrontendEnabled() && enabled && Boolean(projectId && incidentId && runId),
    retry: false,
  });
}

export function useEiExecutionTrace(
  projectId: string,
  incidentId: string | null | undefined,
  runId: string | null | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: eiKeys.executionTrace(projectId, incidentId || '', runId || ''),
    queryFn: () => fetchExecutionTrace(projectId, incidentId!, runId!),
    enabled: eiFrontendEnabled() && enabled && Boolean(projectId && incidentId && runId),
    refetchInterval: () => pollIntervalMs(5_000),
    retry: false,
  });
}

export function useEiValidationReport(
  projectId: string,
  incidentId: string | null | undefined,
  runId: string | null | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: eiKeys.validation(projectId, incidentId || '', runId || ''),
    queryFn: () => fetchValidationReport(projectId, incidentId!, runId!),
    enabled: eiFrontendEnabled() && enabled && Boolean(projectId && incidentId && runId),
    retry: false,
  });
}

export function useEiContextPack(
  projectId: string,
  incidentId: string | null | undefined,
  runId: string | null | undefined,
  enabled = false,
) {
  return useQuery({
    queryKey: eiKeys.contextPack(projectId, incidentId || '', runId || ''),
    queryFn: () => fetchContextPack(projectId, incidentId!, runId!),
    enabled: eiFrontendEnabled() && enabled && Boolean(projectId && incidentId && runId),
    retry: false,
  });
}

export function useEiValidationContract(
  projectId: string,
  incidentId: string | null | undefined,
  runId: string | null | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: eiKeys.validationContract(projectId, incidentId || '', runId || ''),
    queryFn: () => fetchValidationContract(projectId, incidentId!, runId!),
    enabled: eiFrontendEnabled() && enabled && Boolean(projectId && incidentId && runId),
    retry: false,
  });
}

export function useEiConsistencyEvents(projectId: string, enabled = true, limit = 40) {
  return useQuery({
    queryKey: eiKeys.consistencyEvents(projectId, limit),
    queryFn: async () => {
      const raw = await fetchConsistencyEvents(projectId, limit);
      if (Array.isArray(raw)) return raw;
      return raw.events || raw.data || [];
    },
    enabled: eiFrontendEnabled() && enabled && Boolean(projectId),
    refetchInterval: enabled ? 5_000 : false,
    retry: false,
  });
}

export function useEiAuditExport(
  projectId: string,
  incidentId: string | null | undefined,
  runId: string | null | undefined,
  enabled = false,
) {
  return useQuery({
    queryKey: eiKeys.auditExport(projectId, incidentId || '', runId || ''),
    queryFn: () => fetchAuditExport(projectId, incidentId!, runId!),
    enabled: eiFrontendEnabled() && enabled && Boolean(projectId && incidentId && runId),
    retry: false,
  });
}

export function useEngineeringMemorySearch(
  projectId: string,
  query: string,
  opts?: { failure_domain?: string; enabled?: boolean },
) {
  return useQuery({
    queryKey: eiKeys.memorySearch(projectId, query),
    queryFn: () =>
      searchEngineeringMemory(projectId, {
        q: query,
        failure_domain: opts?.failure_domain,
        limit: 8,
      }),
    enabled: eiFrontendEnabled() && Boolean(projectId) && (opts?.enabled ?? true),
    retry: false,
  });
}

export function useBeginEiRun(projectId: string, incidentId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bootstrap?: Record<string, unknown>) =>
      beginEiRun(projectId, incidentId!, bootstrap),
    onSuccess: () => {
      if (incidentId) invalidateEi(qc, projectId, incidentId);
    },
  });
}

export function useEiRunAction(projectId: string, incidentId: string | null | undefined) {
  const qc = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);
  const inFlightRef = useRef<Promise<unknown> | null>(null);
  const bump = () => {
    if (incidentId) invalidateEi(qc, projectId, incidentId);
  };
  const applyRun = (data: { run?: EiInvestigationRun } | EiInvestigationRun | undefined | null) => {
    const run =
      data && typeof data === 'object' && 'run' in data
        ? (data as { run?: EiInvestigationRun }).run
        : (data as EiInvestigationRun | undefined);
    if (run?.id && incidentId) {
      qc.setQueryData(eiKeys.latest(projectId, incidentId), run);
      qc.setQueryData(eiKeys.run(projectId, incidentId, run.id), run);
    }
    bump();
  };
  /** Start a cancellable request. Re-clicks reuse the in-flight promise (do not abort). */
  const withAbort = <T,>(fn: (signal: AbortSignal) => Promise<T>): Promise<T> => {
    if (inFlightRef.current) {
      return inFlightRef.current as Promise<T>;
    }
    const ac = new AbortController();
    abortRef.current = ac;
    const p = fn(ac.signal).finally(() => {
      if (abortRef.current === ac) abortRef.current = null;
      inFlightRef.current = null;
    });
    inFlightRef.current = p;
    return p;
  };
  const cancelInFlight = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    inFlightRef.current = null;
  };
  const onLiveStart = () => {
    // Kick an immediate refresh so the panel does not wait for the first interval tick.
    if (incidentId) {
      void qc.invalidateQueries({ queryKey: eiKeys.latest(projectId, incidentId) });
    }
  };
  return {
    cancelInFlight,
    step: useMutation({
      mutationFn: (runId: string) => withAbort((signal) => stepEiRun(projectId, incidentId!, runId, { signal })),
      onMutate: onLiveStart,
      onSuccess: applyRun,
      onSettled: bump,
    }),
    investigateUntilComplete: useMutation({
      mutationFn: (runId: string) =>
        withAbort((signal) => runUntilInvestigationComplete(projectId, incidentId!, runId, 24, { signal })),
      onMutate: onLiveStart,
      onSuccess: applyRun,
      onSettled: bump,
    }),
    approve: useMutation({
      mutationFn: ({ runId, decision, note, packageHash }: { runId: string; decision?: 'approve' | 'reject' | 'modify'; note?: string; packageHash?: string }) =>
        approveImplementationPackage(projectId, incidentId!, runId, decision ?? 'approve', note ?? '', packageHash),
      onSuccess: bump,
      onSettled: bump,
    }),
    execute: useMutation({
      mutationFn: (runId: string) => executeEiRun(projectId, incidentId!, runId),
      onSuccess: applyRun,
      onSettled: bump,
    }),
    startTesting: useMutation({
      mutationFn: (runId: string) => startEiTesting(projectId, incidentId!, runId),
      onMutate: onLiveStart,
      onSuccess: applyRun,
      onSettled: bump,
    }),
    acceptTesting: useMutation({
      mutationFn: ({ runId, ...input }: { runId: string } & EiAcceptTestingInput) => acceptEiTesting(projectId, incidentId!, runId, input),
      onSuccess: applyRun,
      onSettled: bump,
    }),
    supplyEvidence: useMutation({
      mutationFn: ({ runId, ...input }: { runId: string } & EiSupplyEvidenceInput) =>
        supplyEiIntegrationEvidence(projectId, incidentId!, runId, input),
      onSettled: bump,
    }),
    resolve: useMutation({
      mutationFn: (runId: string) => runUntilResolved(projectId, incidentId!, runId),
      onSuccess: applyRun,
      onSettled: bump,
    }),
    humanGuidance: useMutation({
      mutationFn: ({
        runId,
        decision,
        message,
        choice,
        operator,
        resume,
        answers,
      }: {
        runId: string;
        decision: HumanGuidanceDecision;
        message?: string;
        choice?: string;
        operator?: string;
        resume?: boolean;
        answers?: HumanGuidanceAnswer[];
      }) => submitHumanGuidance(projectId, incidentId!, runId, { decision, message, choice, operator, resume, answers }),
      onMutate: onLiveStart,
      onSuccess: applyRun,
      onSettled: bump,
    }),
    restart: useMutation({
      mutationFn: () => restartEiRun(projectId, incidentId!),
      onSettled: bump,
    }),
  };
}

/** Incident-driven counterpart of restartEiRunForChangeRequest. */
async function restartEiRun(
  projectId: string,
  incidentId: string,
): Promise<{ run_id: string; state: string; incident_id: string }> {
  return apiClient(`${eiBase(projectId, incidentId)}/restart`, {
    method: 'POST',
    timeoutMs: EI_LONG_TIMEOUT_MS,
  });
}

// ─── Change-request-driven missions (proactive, born from an approved
// Engineering Request — no AMS incident involved). Thin URL/query mirrors of
// the incident-keyed functions above, pointed at the CR-scoped route family. ───

export const eiCrKeys = {
  latest: (projectId: string, crId: string) => ['ei-cr', 'latest', projectId, crId] as const,
  package: (projectId: string, crId: string, runId: string) =>
    ['ei-cr', 'package', projectId, crId, runId] as const,
  executionTrace: (projectId: string, crId: string, runId: string) =>
    ['ei-cr', 'execution-trace', projectId, crId, runId] as const,
  validation: (projectId: string, crId: string, runId: string) =>
    ['ei-cr', 'validation', projectId, crId, runId] as const,
  validationContract: (projectId: string, crId: string, runId: string) =>
    ['ei-cr', 'validation-contract', projectId, crId, runId] as const,
  auditExport: (projectId: string, crId: string, runId: string) =>
    ['ei-cr', 'audit-export', projectId, crId, runId] as const,
};

async function fetchLatestEiRunForChangeRequest(
  projectId: string,
  crId: string,
  opts?: { signal?: AbortSignal },
): Promise<EiInvestigationRun | null> {
  try {
    return await apiClient<EiInvestigationRun>(eiCrBase(projectId, crId), {
      timeoutMs: EI_GET_TIMEOUT_MS,
      signal: opts?.signal,
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (/404|not found|disabled|no (investigation )?run/i.test(msg)) return null;
    throw e;
  }
}

async function beginEiRunForChangeRequest(
  projectId: string,
  crId: string,
  bootstrap?: Record<string, unknown>,
): Promise<EiInvestigationRun> {
  return apiClient<EiInvestigationRun>(`${eiCrBase(projectId, crId)}/begin`, {
    method: 'POST',
    body: JSON.stringify({ bootstrap: bootstrap ?? {} }),
  });
}

async function fetchImplementationPackageForChangeRequest(
  projectId: string,
  crId: string,
  runId: string,
): Promise<EiImplementationPackage> {
  return apiClient(`${eiCrBase(projectId, crId)}/runs/${runId}/implementation-package`);
}

async function approveImplementationPackageForChangeRequest(
  projectId: string,
  crId: string,
  runId: string,
  decision: 'approve' | 'reject' | 'modify' = 'approve',
  note = '',
  packageHash?: string,
): Promise<EiInvestigationRun> {
  return apiClient(`${eiCrBase(projectId, crId)}/runs/${runId}/approve`, {
    method: 'POST',
    body: JSON.stringify({ action: decision, decision, note, package_hash: packageHash }),
  });
}

async function executeEiRunForChangeRequest(
  projectId: string,
  crId: string,
  runId: string,
): Promise<{ run?: EiInvestigationRun }> {
  return apiClient(`${eiCrBase(projectId, crId)}/runs/${runId}/execute`, {
    method: 'POST',
    body: '{}',
    timeoutMs: EI_STEP_TIMEOUT_MS,
  });
}

async function startEiTestingForChangeRequest(projectId: string, crId: string, runId: string): Promise<EiInvestigationRun> {
  return apiClient(`${eiCrBase(projectId, crId)}/runs/${runId}/start-testing`, { method: 'POST', body: '{}' });
}

export async function acceptEiTestingForChangeRequest(
  projectId: string,
  crId: string,
  runId: string,
  input: EiAcceptTestingInput,
): Promise<EiInvestigationRun> {
  return apiClient(`${eiCrBase(projectId, crId)}/runs/${runId}/accept-testing`, {
    method: 'POST',
    body: JSON.stringify({
      package_hash: input.packageHash,
      waive: !!input.waive,
      reason: input.reason ?? '',
      acknowledge_supplied_evidence: input.acknowledgeSuppliedEvidence ?? '',
      accept_risks: !!input.acceptRisks,
      report_hash: input.reportHash ?? '',
      acknowledged_risks: input.acknowledgedRisks ?? [],
      enso_utc_report_hash: input.ensoUtcReportHash ?? '',
      enso_utc_acknowledged_gates: input.ensoUtcAcknowledgedGates ?? [],
      enso_utc_reason: input.ensoUtcReason ?? '',
    }),
  });
}

async function runUntilResolvedForChangeRequest(
  projectId: string,
  crId: string,
  runId: string,
  maxSteps = 48,
): Promise<{ run?: EiInvestigationRun }> {
  return apiClient(`${eiCrBase(projectId, crId)}/runs/${runId}/run-until-resolved`, {
    method: 'POST',
    body: JSON.stringify({ max_steps: maxSteps }),
    timeoutMs: EI_LONG_TIMEOUT_MS,
  });
}

/** Every CR-scoped EI query for this change request (prefix match drops the runId). */
export function invalidateEiCr(qc: ReturnType<typeof useQueryClient>, projectId: string, crId: string) {
  void qc.invalidateQueries({ queryKey: ['ei-cr', 'latest', projectId, crId] });
  void qc.invalidateQueries({ queryKey: ['ei-cr', 'package', projectId, crId] });
  void qc.invalidateQueries({ queryKey: ['ei-cr', 'execution-trace', projectId, crId] });
  void qc.invalidateQueries({ queryKey: ['ei-cr', 'validation', projectId, crId] });
  void qc.invalidateQueries({ queryKey: ['ei-cr', 'validation-contract', projectId, crId] });
  void qc.invalidateQueries({ queryKey: ['ei-cr', 'audit-export', projectId, crId] });
}

export function useLatestEiRunForChangeRequest(projectId: string, crId: string | null | undefined) {
  return useQuery({
    queryKey: eiCrKeys.latest(projectId, crId || ''),
    queryFn: ({ signal }) => fetchLatestEiRunForChangeRequest(projectId, crId!, { signal }),
    enabled: eiFrontendEnabled() && Boolean(projectId && crId),
    refetchInterval: (q) => {
      const state = q.state.data?.state || '';
      if (
        /EXECUTING|VALIDATING|ENGINEERING_DESIGN|COLLECTING|GATHERING|FORMING|SELF_REVIEW|PROVISIONAL|DIAGNOS|INVESTIGATION/.test(
          state,
        )
      ) {
        return 2_500;
      }
      return 12_000;
    },
    retry: false,
  });
}

export function useEiImplementationPackageForChangeRequest(
  projectId: string,
  crId: string | null | undefined,
  runId: string | null | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: eiCrKeys.package(projectId, crId || '', runId || ''),
    queryFn: () => fetchImplementationPackageForChangeRequest(projectId, crId!, runId!),
    enabled: eiFrontendEnabled() && enabled && Boolean(projectId && crId && runId),
    retry: false,
  });
}

export function useBeginEiRunForChangeRequest(projectId: string, crId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bootstrap?: Record<string, unknown>) => beginEiRunForChangeRequest(projectId, crId!, bootstrap),
    onSuccess: () => {
      if (crId) invalidateEiCr(qc, projectId, crId);
    },
  });
}

export function useEiRunActionForChangeRequest(projectId: string, crId: string | null | undefined) {
  const qc = useQueryClient();
  const bump = () => {
    if (crId) invalidateEiCr(qc, projectId, crId);
  };
  const applyRun = (data: { run?: EiInvestigationRun } | EiInvestigationRun | undefined | null) => {
    const run =
      data && typeof data === 'object' && 'run' in data
        ? (data as { run?: EiInvestigationRun }).run
        : (data as EiInvestigationRun | undefined);
    if (run?.id && crId) {
      qc.setQueryData(eiCrKeys.latest(projectId, crId), run);
    }
    bump();
  };
  return {
    approve: useMutation({
      mutationFn: ({ runId, decision, note, packageHash }: { runId: string; decision?: 'approve' | 'reject' | 'modify'; note?: string; packageHash?: string }) =>
        approveImplementationPackageForChangeRequest(projectId, crId!, runId, decision ?? 'approve', note ?? '', packageHash),
      onSuccess: bump,
      onSettled: bump,
    }),
    execute: useMutation({
      mutationFn: (runId: string) => executeEiRunForChangeRequest(projectId, crId!, runId),
      onSuccess: applyRun,
      onSettled: bump,
    }),
    startTesting: useMutation({
      mutationFn: (runId: string) => startEiTestingForChangeRequest(projectId, crId!, runId),
      onSuccess: applyRun,
      onSettled: bump,
    }),
    acceptTesting: useMutation({
      mutationFn: ({ runId, ...input }: { runId: string } & EiAcceptTestingInput) =>
        acceptEiTestingForChangeRequest(projectId, crId!, runId, input),
      onSuccess: applyRun,
      onSettled: bump,
    }),
    supplyEvidence: useMutation({
      mutationFn: ({ runId, ...input }: { runId: string } & EiSupplyEvidenceInput) =>
        supplyEiIntegrationEvidenceForChangeRequest(projectId, crId!, runId, input),
      onSettled: bump,
    }),
    resolve: useMutation({
      mutationFn: (runId: string) => runUntilResolvedForChangeRequest(projectId, crId!, runId),
      onSuccess: applyRun,
      onSettled: bump,
    }),
    humanGuidance: useMutation({
      mutationFn: ({
        runId,
        decision,
        message,
        choice,
        operator,
        resume,
        answers,
      }: {
        runId: string;
        decision: HumanGuidanceDecision;
        message?: string;
        choice?: string;
        operator?: string;
        resume?: boolean;
        answers?: HumanGuidanceAnswer[];
      }) =>
        submitHumanGuidanceForChangeRequest(projectId, crId!, runId, {
          decision,
          message,
          choice,
          operator,
          resume,
          answers,
        }),
      onMutate: bump,
      onSuccess: applyRun,
      onSettled: bump,
    }),
    restart: useMutation({
      mutationFn: () => restartEiRunForChangeRequest(projectId, crId!),
      onSettled: bump,
    }),
  };
}

/**
 * Restarts the mission: reverts the prior run's workspace when a pre-mutate
 * snapshot exists, disposes its working tree, force-closes that run, and
 * begins a fresh InvestigationRun (becomes latest by updated_at).
 */
async function restartEiRunForChangeRequest(
  projectId: string,
  crId: string,
): Promise<{ run_id: string; state: string; change_request_id: string }> {
  return apiClient(`${eiCrBase(projectId, crId)}/restart`, {
    method: 'POST',
    timeoutMs: EI_LONG_TIMEOUT_MS,
  });
}

async function fetchExecutionTraceForChangeRequest(
  projectId: string,
  crId: string,
  runId: string,
): Promise<EiExecutionTracePayload> {
  return apiClient(`${eiCrBase(projectId, crId)}/runs/${runId}/execution-trace`);
}

async function fetchValidationReportForChangeRequest(
  projectId: string,
  crId: string,
  runId: string,
): Promise<EiValidationReport> {
  return apiClient(`${eiCrBase(projectId, crId)}/runs/${runId}/validation-report`);
}

/** Execution tab's CR-scoped counterpart of useEiExecutionTrace — same dual-key backend route family. */
export function useEiExecutionTraceForChangeRequest(
  projectId: string,
  crId: string | null | undefined,
  runId: string | null | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: eiCrKeys.executionTrace(projectId, crId || '', runId || ''),
    queryFn: () => fetchExecutionTraceForChangeRequest(projectId, crId!, runId!),
    enabled: eiFrontendEnabled() && enabled && Boolean(projectId && crId && runId),
    refetchInterval: () => pollIntervalMs(5_000),
    retry: false,
  });
}

/** Execution tab's CR-scoped counterpart of useEiValidationReport. */
export function useEiValidationReportForChangeRequest(
  projectId: string,
  crId: string | null | undefined,
  runId: string | null | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: eiCrKeys.validation(projectId, crId || '', runId || ''),
    queryFn: () => fetchValidationReportForChangeRequest(projectId, crId!, runId!),
    enabled: eiFrontendEnabled() && enabled && Boolean(projectId && crId && runId),
    retry: false,
  });
}

/** CR-scoped validation contract (route: …/change-requests/:crId/engineering-intelligence/runs/:runId/validation-contract). */
export function useEiValidationContractForChangeRequest(
  projectId: string,
  crId: string | null | undefined,
  runId: string | null | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: eiCrKeys.validationContract(projectId, crId || '', runId || ''),
    queryFn: async (): Promise<EiValidationContract | null> => {
      try {
        return await apiClient(`${eiCrBase(projectId, crId!)}/runs/${runId}/validation-contract`);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        if (/404|not found|not expos|incomplete/i.test(msg)) return null;
        throw e;
      }
    },
    enabled: eiFrontendEnabled() && enabled && Boolean(projectId && crId && runId),
    retry: false,
  });
}

/** CR-scoped audit export (route: …/change-requests/:crId/engineering-intelligence/runs/:runId/audit-export). */
export async function fetchAuditExportForChangeRequest(
  projectId: string,
  crId: string,
  runId: string,
): Promise<EiAuditExport> {
  return apiClient(`${eiCrBase(projectId, crId)}/runs/${runId}/audit-export`);
}

/**
 * Downloads the EI audit export for a run as a JSON file. Incident- and
 * CR-scoped missions share the same document shape, only the route differs.
 */
export async function downloadEiAuditExport(input: {
  projectId: string;
  runId: string;
  incidentId?: string | null;
  crId?: string | null;
}): Promise<void> {
  const doc = input.crId
    ? await fetchAuditExportForChangeRequest(input.projectId, input.crId, input.runId)
    : await fetchAuditExport(input.projectId, input.incidentId!, input.runId);
  const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ei-audit-${input.runId}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Whether the sealed package is safe to fetch / display for operators.
 * Mirrors backend CanExposeImplementationPackage — mid-design states are
 * intentionally excluded so the UI keeps showing "generating…" instead of
 * falling back to the coarse Engineering Request template. */
export function canShowImplementationPackage(state?: string | null): boolean {
  if (!state) return false;
  const s = String(state).toUpperCase();
  return [
    'IMPLEMENTATION_PACKAGE_READY',
    'AWAITING_APPROVAL',
    'EXECUTING',
    'ROLLING_BACK',
    'EXECUTION_BLOCKED',
    'CONVERGING',
    'VALIDATING',
    'AGENT_REFLECTION',
    'EVALUATE_SUCCESS',
    'RESOLVED',
    'LEARNING',
    'REFLECTION',
    'KNOWLEDGE_UPDATE',
    'CLOSED',
    'NEEDS_HUMAN',
  ].includes(s);
}

export function canShowExecution(state?: string | null): boolean {
  if (!state) return false;
  // NEEDS_HUMAN deliberately excluded: it fires for missions that never
  // reached execution at all (e.g. an infrastructure/budget failure during
  // investigation or plan generation) just as often as for one paused
  // mid-execution — the state alone can't tell them apart. Every caller of
  // this function already (or now) also checks execution_trace.length,
  // which is the real, present-or-absent signal for "execution actually
  // started" — certified live: a run that escalated during ENGINEERING_DESIGN
  // with zero execution history rendered "Repository Successfully Updated"
  // over its untouched, freshly-materialized workspace.
  return [
    'EXECUTING',
    'VALIDATING',
    'AGENT_REFLECTION',
    'EVALUATE_SUCCESS',
    // Tested on the working copy; results (or Execute) wait for the operator.
    'TESTS_READY',
    'READY_TO_EXECUTE',
    'CONVERGING',
    'RESOLVED',
    'LEARNING',
    'REFLECTION',
    'KNOWLEDGE_UPDATE',
    'CLOSED',
  ].includes(state);
}

// ─── Repository Transformation Workspace ───────────────────────────────────
// The operator reviews the TRANSFORMED REPOSITORY itself (a real, on-disk
// post-execution workspace — see backend WorkspaceRef) rather than Git
// operations. CR-scoped only for now — the same handlers exist on the
// incident-scoped route family backend-side (shared, runId-only handlers)
// but aren't wired to a frontend hook yet, since Execution History is
// inherently a change-request concept in this codebase (no equivalent
// "list past runs for an incident" backend method exists).

export interface EiRepositoryTreeNode {
  name: string;
  path: string;
  is_dir: boolean;
  change_type?: 'added' | 'modified' | 'deleted' | 'unchanged';
  lines_changed?: number;
  confidence?: number;
  children?: EiRepositoryTreeNode[];
}

export interface EiRepositoryFile {
  path: string;
  change_type: string;
  before: string;
  before_source: string;
  after: string;
}

export interface EiExecutionSummary {
  run_id: string;
  state: string;
  updated_at: string;
  confidence?: number;
  has_implementation_package: boolean;
  executed: boolean;
}

export interface EiTimelineMilestone {
  label: string;
  at?: string;
  status: 'done' | 'pending';
  detail?: string;
}

const rtwKeys = {
  tree: (projectId: string, scopeKey: string, runId: string) => ['ei-rtw', 'tree', projectId, scopeKey, runId] as const,
  file: (projectId: string, scopeKey: string, runId: string, path: string, vs = 'pre_mutate') =>
    ['ei-rtw', 'file', projectId, scopeKey, runId, path, vs] as const,
  history: (projectId: string, scopeKey: string) => ['ei-rtw', 'history', projectId, scopeKey] as const,
  timeline: (projectId: string, scopeKey: string, runId: string) =>
    ['ei-rtw', 'timeline', projectId, scopeKey, runId] as const,
};

function eiRtwBase(projectId: string, scope: { crId?: string | null; incidentId?: string | null }): string | null {
  if (scope.crId) return eiCrBase(projectId, scope.crId);
  if (scope.incidentId) return eiBase(projectId, scope.incidentId);
  return null;
}

function rtwScopeKey(scope: { crId?: string | null; incidentId?: string | null }): string {
  return scope.crId ? `cr:${scope.crId}` : scope.incidentId ? `inc:${scope.incidentId}` : '';
}

export function useRepositoryTree(
  projectId: string,
  scope: { crId?: string | null; incidentId?: string | null },
  runId: string | null | undefined,
) {
  const base = eiRtwBase(projectId, scope);
  const scopeKey = rtwScopeKey(scope);
  return useQuery({
    queryKey: rtwKeys.tree(projectId, scopeKey, runId || ''),
    queryFn: () =>
      apiClient<{ tree: EiRepositoryTreeNode; workspace_ref?: { working_tree?: string } }>(
        `${base}/runs/${runId}/repository/tree`,
      ),
    enabled: Boolean(projectId && base && runId),
    retry: false,
  });
}

export function useRepositoryFile(
  projectId: string,
  scope: { crId?: string | null; incidentId?: string | null },
  runId: string | null | undefined,
  path: string | null | undefined,
  opts?: { vs?: 'pre_mutate' | 'snapshot' | 'patch' },
) {
  const vs = opts?.vs ?? 'pre_mutate';
  const base = eiRtwBase(projectId, scope);
  const scopeKey = rtwScopeKey(scope);
  return useQuery({
    queryKey: rtwKeys.file(projectId, scopeKey, runId || '', path || '', vs),
    queryFn: () =>
      apiClient<EiRepositoryFile>(
        `${base}/runs/${runId}/repository/file?path=${encodeURIComponent(path!)}&vs=${encodeURIComponent(vs)}`,
      ),
    enabled: Boolean(projectId && base && runId && path),
    retry: false,
  });
}

export function useExecutionHistory(
  projectId: string,
  scope: { crId?: string | null; incidentId?: string | null },
) {
  // History listing is CR-scoped on the API today.
  const crId = scope.crId;
  return useQuery({
    queryKey: rtwKeys.history(projectId, crId || ''),
    queryFn: ({ signal }) => apiClient<{ executions: EiExecutionSummary[] }>(`${eiCrBase(projectId, crId!)}/history`, { signal }),
    enabled: Boolean(projectId && crId),
    retry: false,
  });
}

export function useExecutionTimeline(
  projectId: string,
  scope: { crId?: string | null; incidentId?: string | null },
  runId: string | null | undefined,
) {
  const base = eiRtwBase(projectId, scope);
  const scopeKey = rtwScopeKey(scope);
  return useQuery({
    queryKey: rtwKeys.timeline(projectId, scopeKey, runId || ''),
    queryFn: ({ signal }) =>
      apiClient<{ milestones: EiTimelineMilestone[] }>(`${base}/runs/${runId}/timeline`, { signal }),
    enabled: Boolean(projectId && base && runId),
    retry: false,
  });
}

/**
 * Sealed engineering patch (search/replace text) for a run.
 * Prefers the project-scoped route (no incident/CR required). Falls back to
 * the incident/CR download endpoint when a scope is available.
 */
export async function fetchEngineeringPatch(
  projectId: string,
  runId: string,
  scope?: { crId?: string | null; incidentId?: string | null },
): Promise<EiPatchPlan | null> {
  try {
    const data = await apiClient<EiPatchPlan>(
      `/projects/${projectId}/engineering-intelligence/runs/${runId}/patch`,
    );
    if (data && Array.isArray(data.files)) return data;
  } catch {
    /* fall through to scoped download */
  }

  const base = scope ? eiRtwBase(projectId, scope) : null;
  if (!base) return null;
  const res = await fetchWithTimeout(`/api/v1${base}/runs/${runId}/repository/download?kind=patch`, {
    method: 'GET',
    headers: buildApiHeaders({ Accept: 'application/json' }),
    timeoutMs: 60_000,
  });
  if (!res.ok) return null;
  try {
    return (await res.json()) as EiPatchPlan;
  } catch {
    return null;
  }
}

export function useEngineeringPatch(
  projectId: string,
  runId: string | null | undefined,
  scope?: { crId?: string | null; incidentId?: string | null },
) {
  const scopeKey = scope ? rtwScopeKey(scope) : '';
  return useQuery({
    queryKey: ['ei-rtw', 'patch', projectId, scopeKey, runId || ''] as const,
    queryFn: () => fetchEngineeringPatch(projectId, runId!, scope),
    enabled: Boolean(projectId && runId),
    staleTime: 60_000,
    retry: false,
  });
}

/** Full requested prompt for a sealed run (Goal.Statement) — project-scoped. */
export async function fetchEngineeringRunPrompt(
  projectId: string,
  runId: string,
): Promise<{ prompt?: string; statement?: string; run_id?: string } | null> {
  try {
    return await apiClient<{ prompt?: string; statement?: string; run_id?: string }>(
      `/projects/${projectId}/engineering-intelligence/runs/${runId}/prompt`,
    );
  } catch {
    return null;
  }
}

export function useEngineeringRunPrompt(
  projectId: string,
  runId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['ei-rtw', 'prompt', projectId, runId || ''] as const,
    queryFn: () => fetchEngineeringRunPrompt(projectId, runId!),
    enabled: Boolean(projectId && runId),
    staleTime: 60_000,
    retry: false,
  });
}

/** Direct browser-navigable download URL (a real zip/JSON stream, not a
 * fetch response) — includes the API key as a query param since a plain
 * anchor download can't send a custom header. Prefer
 * {@link downloadRepositoryWorkspace} from the UI so auth goes via header. */
export function repositoryDownloadUrl(
  projectId: string,
  crId: string,
  runId: string,
  kind: 'repository' | 'changed-files' | 'patch' | 'rollback' | 'report',
): string {
  return `/api/v1${eiCrBase(projectId, crId)}/runs/${runId}/repository/download?kind=${kind}${downloadCredentialQuery()}`;
}

export function repositoryDownloadUrlForIncident(
  projectId: string,
  incidentId: string,
  runId: string,
  kind: 'repository' | 'changed-files' | 'patch' | 'rollback' | 'report',
): string {
  return `/api/v1${eiBase(projectId, incidentId)}/runs/${runId}/repository/download?kind=${kind}${downloadCredentialQuery()}`;
}

const DOWNLOAD_FILENAMES: Record<string, (runId: string) => string> = {
  repository: (id) => `repository-${id}.zip`,
  'changed-files': (id) => `changed-files-${id}.zip`,
  patch: (id) => `patch-${id}.json`,
  rollback: (id) => `rollback-${id}.json`,
  report: (id) => `implementation-report-${id}.json`,
};

/** Authenticated repository download — sends X-API-Key so the browser does not
 * need to navigate to a raw API URL (which previously 401'd without headers). */
export async function downloadRepositoryWorkspace(opts: {
  projectId: string;
  runId: string;
  kind: 'repository' | 'changed-files' | 'patch' | 'rollback' | 'report';
  crId?: string;
  incidentId?: string;
}): Promise<void> {
  const { projectId, runId, kind, crId, incidentId } = opts;
  const base = crId
    ? eiCrBase(projectId, crId)
    : incidentId
      ? eiBase(projectId, incidentId)
      : '';
  if (!base) throw new Error('download requires a change request or incident id');

  const endpoint = `${base}/runs/${runId}/repository/download?kind=${encodeURIComponent(kind)}`;
  const res = await fetchWithTimeout(`/api/v1${endpoint}`, {
    method: 'GET',
    headers: buildApiHeaders({ Accept: '*/*' }),
    timeoutMs: 600_000,
  });

  if (!res.ok) {
    let message = `Download failed (HTTP ${res.status})`;
    try {
      const body = (await res.json()) as { error?: { message?: string; details?: string } };
      message = body.error?.details || body.error?.message || message;
    } catch {
      // non-JSON error body
    }
    throw new Error(message);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = DOWNLOAD_FILENAMES[kind]?.(runId) || `download-${runId}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
